import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react'
import { useTranslation } from 'react-i18next'
import { useAtomValue } from 'jotai'
import { CheckedState } from '@radix-ui/react-checkbox'

import {
  TwitterAuthExpired,
  TwitterAuthOccupied,
  TwitterAuthSuccess
} from '@/assets/images/auth'
import { HttpCode, LoginStatus, STORAGE_KEY } from '@/constants/common'
import ENV_PARAMS from '@/constants/envParams'
import {
  TwitterAuthState,
  useGetTwitterAuthLink
} from '@/hooks/services/useTwitterAuth'
import { useUserInfo } from '@/hooks/services/useUserInfo'
import { toast } from '@/hooks/useToastify'
import { useWallet } from '@/hooks/useWallet'
import { IAuthResult } from '@/lib/types'
import { loginStatusAtom, twitterAuthStateAtom } from '@/stores/services'

import { Flex } from './common/Box'
import { AsyncButton, Button } from './common/Button'
import Checkbox from './common/Checkbox'
import { ProDialog } from './common/Dialog'
import { FileRocket } from './common/Lottie'
import { Text } from './common/Text'

const TOAST_OPTIONS = { autoClose: 5000 }

const TwitterAuth = React.forwardRef<
  React.ComponentRef<typeof ProDialog> & {
    authTwitter: (callback?: (data: any) => void) => void
  }
>((_, ref) => {
  const { t } = useTranslation()
  const { connect: connectWallet } = useWallet()
  const { mutate: getUserInfo } = useUserInfo()
  const { trigger: getTwitterAuthLink } = useGetTwitterAuthLink()

  const loginStatus = useAtomValue(loginStatusAtom)
  const twitterAuthState = useAtomValue(twitterAuthStateAtom)

  const [agree, setAgree] = useState<CheckedState>(false)
  const [open, setOpen] = useState(false)
  const [checkLoading, setCheckLoading] = useState(false)
  const [errorMsg, setErrorMsg] = useState('')
  const [displayState, setDisplayState] = useState<TwitterAuthState>()
  const [authUrl, setAuthUrl] = useState<string>()

  const newWindowRef = useRef<WindowProxy | null>(null)
  const timerOpenWindowRef = useRef<NodeJS.Timeout>()
  const messageHandlerRef = useRef<any>()
  // 授权成功回调
  const callbackFnRef = useRef<any>()
  const openStateRef = useRef(false)
  // 回调的状态优先级高于AuthState，当关闭弹窗时重置为undefined
  const callbackStateRef = useRef<TwitterAuthState>()

  const titleMap = {
    [TwitterAuthState.UN_KNOWN]: t('twitter.auth.dialog.title'),
    [TwitterAuthState.UNAUTHENTICATED]: t('twitter.auth.dialog.title'),
    [TwitterAuthState.AUTHENTICATED]: t('twitter.auth.success'),
    [TwitterAuthState.REDIRECTING]: t('twitter.auth.pending'),
    [TwitterAuthState.EXPIRED]: t('twitter.auth.expired'),
    [TwitterAuthState.OCCUPIED]: t('twitter.auth.occupied'),
    [TwitterAuthState.FAILED]: t('twitter.auth.linkFailed')
  }

  const removeEvents = useCallback(() => {
    if (timerOpenWindowRef.current) {
      clearInterval(timerOpenWindowRef.current)
    }
    if (messageHandlerRef.current) {
      window.removeEventListener('message', messageHandlerRef.current)
    }
    if (newWindowRef.current && !newWindowRef.current.closed) {
      newWindowRef.current.close()
      newWindowRef.current = null
    }
  }, [])

  const openAuth = useCallback(() => {
    setOpen(true)
    openStateRef.current = true
    if (!authUrl) {
      getTwitterAuthLink({ domainType: 1 }).then(({ data }) => {
        setAuthUrl(data?.authUrl)
      })
    }
  }, [authUrl, getTwitterAuthLink])

  const closeAuth = useCallback(() => {
    removeEvents()
    setOpen(false)
    setDisplayState(undefined)
    openStateRef.current = false
    callbackFnRef.current = undefined
    callbackStateRef.current = undefined
  }, [removeEvents])

  const handleWindowCallback = useCallback(() => {
    // 小窗口已打开，监听小窗口消息
    timerOpenWindowRef.current = setInterval(() => {
      if (newWindowRef.current?.closed) {
        closeAuth()
        setCheckLoading(false)
        toast.error('Authorization was cancelled.', TOAST_OPTIONS)
      }
    }, 1000)
    // 声明message处理函数
    messageHandlerRef.current = (event: MessageEvent<IAuthResult>) => {
      if (event.origin !== ENV_PARAMS.SITE_DOMAIN) {
        return
      }
      if (event.data?.type !== 'twitter') {
        return
      }
      const authResult = event.data
      if (authResult.success === true) {
        removeEvents()
        setCheckLoading(false)
        callbackStateRef.current = TwitterAuthState.AUTHENTICATED
        setDisplayState(callbackStateRef.current)
        getUserInfo()
        if (callbackFnRef.current) {
          callbackFnRef.current(authResult.result?.data)
        }
      } else if (authResult.success === false) {
        removeEvents()
        setCheckLoading(false)
        if (authResult.result?.code == HttpCode.TwitterOccupied) {
          callbackStateRef.current = TwitterAuthState.OCCUPIED
          setDisplayState(TwitterAuthState.OCCUPIED)
        } else {
          callbackStateRef.current = TwitterAuthState.FAILED
          setDisplayState(TwitterAuthState.FAILED)
          setErrorMsg(authResult.result?.msg || '')
        }
        toast.error(authResult.result?.msg, TOAST_OPTIONS)
      }
    }
    window.addEventListener('message', messageHandlerRef.current)
  }, [removeEvents, getUserInfo, setDisplayState, closeAuth])

  // 重定向授权
  const handleRedirectCallback = useCallback(() => {
    let authResult: IAuthResult | undefined
    // storage数据读取后删除
    const storeResult = window.localStorage.getItem(STORAGE_KEY.AUTH_RESULT)
    if (storeResult) {
      try {
        authResult = JSON.parse(storeResult)
      } catch (_) {
        authResult = undefined
      }
    }
    if (!authResult) {
      return
    }
    // console.log('authRoute', authRoute)
    // console.log('authResult', authResult)
    if (authResult.type !== 'twitter') {
      return
    }
    window.localStorage.removeItem(STORAGE_KEY.AUTH_RESULT)
    window.localStorage.removeItem(STORAGE_KEY.BEFORE_AUTH_ROUTE)
    if (Date.now() - (authResult.timestamp || 0) > 30000) {
      return
    }
    if (authResult.success === true) {
      getUserInfo()
      callbackStateRef.current = TwitterAuthState.AUTHENTICATED
      setDisplayState(TwitterAuthState.AUTHENTICATED)
      openAuth()
    } else {
      if (authResult.result?.code == HttpCode.TwitterOccupied) {
        callbackStateRef.current = TwitterAuthState.OCCUPIED
        setDisplayState(TwitterAuthState.OCCUPIED)
      } else {
        callbackStateRef.current = TwitterAuthState.FAILED
        setDisplayState(TwitterAuthState.FAILED)
        setErrorMsg(authResult.result?.msg || '')
      }
      openAuth()
    }
  }, [openAuth, setDisplayState, getUserInfo])

  // 打开授权页面
  const handleComfirmAuth = useCallback(async () => {
    if (loginStatus !== LoginStatus.LOGIN) {
      connectWallet()
      return
    }
    setDisplayState(TwitterAuthState.REDIRECTING)
    setCheckLoading(true)
    openAuth()
    try {
      let authLink = authUrl
      if (!authUrl) {
        const { data } = await getTwitterAuthLink({ domainType: 1 })
        authLink = data?.authUrl
        setAuthUrl(authLink)
      }
      if (!openStateRef.current) {
        // 已取消授权
        return
      }
      if (!authLink) {
        throw 'Unknow auth url'
      }
      // 先清空事件
      removeEvents()
      newWindowRef.current = window.open(
        authLink,
        '_blank',
        'width=600,height=800,toolbar=no,menubar=no,scrollbars=yes,resizable=yes'
      )

      if (newWindowRef.current) {
        // 小窗口已打开，监听小窗口消息
        handleWindowCallback()
      } else {
        // 小窗口未能打开，使用重定向方式
        handleSaveUrl()
        window.location.href = authLink
      }
    } catch (err: any) {
      setCheckLoading(false)
      toast.error(err?.message || err?.msg || err?.toString(), TOAST_OPTIONS)
    }
  }, [
    authUrl,
    loginStatus,
    connectWallet,
    removeEvents,
    getTwitterAuthLink,
    handleWindowCallback,
    openAuth
  ])

  const handleSaveUrl = () => {
    window.localStorage.setItem(STORAGE_KEY.BEFORE_AUTH_ROUTE, location.href)
  }

  useEffect(() => {
    handleRedirectCallback()
    return () => {
      callbackFnRef.current = undefined
      removeEvents()
    }
  }, [])

  useEffect(() => {
    if (open && !callbackStateRef.current) {
      setDisplayState(twitterAuthState)
    }
  }, [open, twitterAuthState])

  useImperativeHandle(
    ref,
    () => ({
      authTwitter: (callback) => {
        callbackFnRef.current = callback
        setDisplayState(TwitterAuthState.UNAUTHENTICATED)
        openAuth()
      },
      open: () => openAuth(),
      close: () => closeAuth()
    }),
    [setDisplayState, openAuth, closeAuth]
  )

  return (
    <ProDialog
      open={open}
      title={
        <span className="text-base">
          {displayState
            ? titleMap[displayState]
            : titleMap[TwitterAuthState.UN_KNOWN]}
        </span>
      }
      contentProps={{
        className: '!w-[90%] !max-w-[480px] gap-2 xl:gap-6'
      }}
      onClose={closeAuth}
    >
      {displayState === TwitterAuthState.UNAUTHENTICATED ? (
        <>
          <div className="[&_p]:text-sm">
            <Text className="text-sm">
              {t('twitter.auth.dialog.description.p1')}
            </Text>
            <Text className="text-sm">
              {t('twitter.auth.dialog.description.p2')}
            </Text>
            {[
              t('twitter.auth.dialog.description.l1'),
              t('twitter.auth.dialog.description.l2'),
              t('twitter.auth.dialog.description.l3')
            ].map((text, index) => (
              <Text key={index} className="pl-2 text-sm">
                · {text}
              </Text>
            ))}
            {/* <Text>{t('twitter.auth.dialog.description.end')}</Text> */}
            <Flex className="mt-4 items-center gap-2 xl:mt-8">
              <Checkbox
                id="twitter-auth"
                className="size-4"
                checked={agree}
                onCheckedChange={(checked) => setAgree(checked)}
              />
              <label htmlFor="twitter-auth" className="text-sm">
                {t('twitter.auth.dialog.agree')}
              </label>
            </Flex>
          </div>
          <AsyncButton
            connection
            className="font-Orbitron !flex"
            disabled={!agree}
            onClick={() => handleComfirmAuth()}
          >
            {t('twitter.auth.connect')}
          </AsyncButton>
        </>
      ) : displayState === TwitterAuthState.REDIRECTING ? (
        <>
          <div className="mx-auto py-4">
            <FileRocket />
          </div>
          <Text className="text-sm">
            {t('twitter.auth.pending.description')}
          </Text>
          <AsyncButton
            className="font-Orbitron !flex"
            disabled={checkLoading}
            loading={checkLoading}
            onClick={() => handleComfirmAuth()}
          >
            {t('common.confirm')}
          </AsyncButton>
          {/* <div>
            <p>
              {t('auth.message.noOpen')}&nbsp;
              <a
                onClick={handleRedirectAuth}
                className="cursor-pointer text-primary underline"
              >
                {t('auth.message.clickOpen')}
              </a>
            </p>
          </div> */}
        </>
      ) : displayState === TwitterAuthState.AUTHENTICATED ? (
        <>
          <img
            src={TwitterAuthSuccess}
            alt="Twitter Auth Success"
            className="mx-auto h-[136px]"
          />
          <Text className="text-sm">
            {t('twitter.auth.success.description')}
          </Text>
          <AsyncButton className="font-Orbitron !flex" onClick={closeAuth}>
            {t('common.confirm')}
          </AsyncButton>
        </>
      ) : displayState === TwitterAuthState.EXPIRED ? (
        <>
          <img src={TwitterAuthExpired} className="mx-auto h-[136px]" />
          <Text className="text-sm">
            {t('twitter.auth.expired.description')}
          </Text>
          <AsyncButton
            connection
            className="font-Orbitron !flex"
            onClick={() => handleComfirmAuth()}
          >
            {t('twitter.auth.reconnect')}
          </AsyncButton>
        </>
      ) : displayState === TwitterAuthState.OCCUPIED ? (
        <>
          <img src={TwitterAuthOccupied} className="mx-auto h-[136px]" />
          <Text className="text-sm">
            {t('twitter.auth.occupied.description')}
          </Text>
          <AsyncButton className="font-Orbitron !flex" onClick={closeAuth}>
            {t('common.confirm')}
          </AsyncButton>
        </>
      ) : displayState === TwitterAuthState.FAILED ? (
        <>
          <img src={TwitterAuthExpired} className="mx-auto h-[136px]" />
          <Text className="text-sm">
            <span className="capitalize">{errorMsg}&nbsp;</span>
            {t('auth.message.linkAgain')}
          </Text>
          <Flex className="gap-4">
            <Button
              className="font-Orbitron h-12 w-0 flex-1 text-base xl:h-12 xl:text-lg"
              variant={'revert'}
              onClick={closeAuth}
            >
              {t('common.cancel')}
            </Button>
            <AsyncButton
              connection
              className="font-Orbitron !flex w-0 flex-1"
              onClick={() => handleComfirmAuth()}
            >
              {t('twitter.auth.reconnect')}
            </AsyncButton>
          </Flex>
        </>
      ) : undefined}
    </ProDialog>
  )
})

export default TwitterAuth
